import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Files, 
  UploadSimple, 
  FilePdf, 
  FileDoc, 
  FileXls, 
  FileText,
  Trash,
  Eye,
  MagnifyingGlass,
  CircleNotch,
  CheckCircle,
  XCircle,
  Clock
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploadé avec succès");
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, docName) => {
    if (!window.confirm(`Supprimer "${docName}" ?`)) return;

    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document supprimé");
      fetchDocuments();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf": return <FilePdf weight="duotone" className="w-8 h-8 text-red-400" />;
      case "docx": return <FileDoc weight="duotone" className="w-8 h-8 text-blue-400" />;
      case "xlsx": return <FileXls weight="duotone" className="w-8 h-8 text-green-400" />;
      default: return <FileText weight="duotone" className="w-8 h-8 text-slate-400" />;
    }
  };

  const getStatusBadge = (status, score) => {
    switch (status) {
      case "completed":
        return (
          <span className="flex items-center gap-1 text-cyber-success text-xs font-mono">
            <CheckCircle weight="fill" className="w-4 h-4" />
            {score !== null ? `${score}%` : "Analysé"}
          </span>
        );
      case "analyzing":
        return (
          <span className="flex items-center gap-1 text-cyber-volt text-xs font-mono">
            <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
            Analyse...
          </span>
        );
      case "failed":
        return (
          <span className="flex items-center gap-1 text-cyber-danger text-xs font-mono">
            <XCircle weight="fill" className="w-4 h-4" />
            Échec
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-slate-400 text-xs font-mono">
            <Clock weight="fill" className="w-4 h-4" />
            En attente
          </span>
        );
    }
  };

  return (
    <div data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Documents</h1>
          <p className="text-slate-400">Gérez et analysez vos documents ISO 27001</p>
        </div>
      </div>

      {/* Upload Zone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div
          className={`dropzone ${dragActive ? "active" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload').click()}
          data-testid="upload-dropzone"
        >
          <input
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.docx,.xlsx,.txt,.csv,.md"
            className="sr-only"
            id="file-upload"
            disabled={uploading}
            data-testid="file-input"
          />
          <div className="flex flex-col items-center pointer-events-none">
            {uploading ? (
              <CircleNotch weight="bold" className="w-12 h-12 text-cyber-primary animate-spin mb-4" />
            ) : (
              <UploadSimple weight="duotone" className="w-12 h-12 text-cyber-primary mb-4" />
            )}
            <p className="text-lg font-medium mb-2">
              {uploading ? "Upload en cours..." : "Glissez-déposez vos fichiers ici"}
            </p>
            <p className="text-sm text-slate-400">
              ou <span className="text-cyber-primary">cliquez pour parcourir</span> vos fichiers
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Formats supportés: PDF, Word, Excel, TXT, CSV, Markdown
            </p>
          </div>
        </div>
      </motion.div>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-cyber-primary font-mono">Chargement...</div>
        </div>
      ) : documents.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Files weight="duotone" className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Aucun document</h3>
          <p className="text-slate-400">Uploadez votre premier document pour commencer</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {documents.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="cyber-card p-4 group"
                data-testid={`document-card-${doc.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyber-bg rounded-sm flex items-center justify-center shrink-0">
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate mb-1" title={doc.name}>
                      {doc.name}
                    </h3>
                    <p className="text-xs text-slate-500 font-mono mb-2">
                      Par {doc.uploaded_by_name}
                    </p>
                    {getStatusBadge(doc.analysis_status, doc.compliance_score)}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-cyber-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                    className="flex-1 text-slate-400 hover:text-cyber-primary hover:bg-cyber-primary/10"
                    data-testid={`view-doc-${doc.id}`}
                  >
                    <Eye weight="duotone" className="w-4 h-4 mr-2" />
                    Voir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(doc.id, doc.name)}
                    className="text-slate-400 hover:text-cyber-danger hover:bg-cyber-danger/10"
                    data-testid={`delete-doc-${doc.id}`}
                  >
                    <Trash weight="duotone" className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
